import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatSnackBar } from '@angular/material';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BusinessService } from '../models/business-service.model';
import { BusinessFeesService } from '../services/business-fees.service';


@Component({
  selector: 'app-edit-fee',
  templateUrl: './edit-fee.component.html',
  styleUrls: ['./edit-fee.component.scss']
})
export class EditFeeComponent implements OnInit {
  fee: BusinessService;
  originalFee: BusinessService;
  isNew = false;
  isLoading = false;
  busID: number;
  itemsToChange: Set<number>;
  formGroup: FormGroup;
  selectedDescription: string;
  selectedFeeBasis: string;
  selectedFee: string;
  selectedNote: string;
  selectedAircraftType: string;
  selectedWaiver: boolean;
  selectedWaiverFuelQty: string;
  selectedFeeRadio: string;
  feeTypes: string [] = new Array<string>();
  feeBasisVals = ['Per Use', 'Hourly', '12 Hour',
    '24 Hour', 'Weekly', 'Monthly', 'Annual', 'No Fee', 'Fee Not Disclosed'];
  aircraftTypes: string [] = new Array<string>();

  constructor(private _businessFeeService: BusinessFeesService,
              private _matDialogRef: MatDialogRef<EditFeeComponent>,
              public _snackBar: MatSnackBar,
              @Inject(MAT_DIALOG_DATA) data) {
    this.fee = data.fee || new BusinessService();
    this.isNew = data.isNew || false;
    this.busID = data.busID;
    this.originalFee = Object.assign(new BusinessService(), data.fee);
    this.itemsToChange = new Set<number> ();
    this.selectedDescription = this.fee.description;
    this.selectedFeeBasis = BusinessService.formatFeeBasis(this.fee.feeBasis);
    // this.selectedFee = this.fee.fee ? this.fee.fee.toFixed(2) : '0.00';
    if (this.fee.fee) {
      if (Number.isInteger(this.fee.fee)) {
        this.selectedFee = this.fee.fee.toFixed(0);
      } else {
        this.selectedFee = this.fee.fee.toFixed(2);
      }
    } else {
      this.selectedFee = '0';
    }
    this.selectedNote = this.fee.serviceNote ? this.fee.serviceNote : '';
    this.selectedAircraftType = this.fee.aircraftType;
    this.selectedWaiverFuelQty = this.fee.waiverFuelQty ? this.fee.waiverFuelQty.toString() : '';
    if (this.isNew) {
      this.selectedWaiver = false;
    } else {
      this.selectedWaiver = this.fee.waiver;
    }
  }

  async ngOnInit() {
    this._matDialogRef.backdropClick().subscribe(() => {
      this.close({cancelBtnClicked: true});
    });
    this.formGroup = new FormGroup({
      description: new FormControl(this.selectedDescription, [Validators.required]),
      feeBasis: new FormControl({value: this.selectedFeeBasis, disabled: false}, [Validators.required]),
      fee: new FormControl({value: this.selectedFee, disabled: false}, [Validators.min(.01), Validators.maxLength(6),
        Validators.pattern('\\d*(\\.\\d\\d)?$')]),
      note: new FormControl(this.selectedNote, [Validators.maxLength(500)]),
      aircraftType: new FormControl(this.selectedAircraftType, [Validators.required]),
      waiver: new FormControl(this.selectedWaiver, [Validators.required]),
      waiverFuelQty: new FormControl(this.selectedWaiverFuelQty, [Validators.maxLength(6),
        Validators.pattern('\\d+')]),
      feeRadio: new FormControl(this.selectedFeeRadio, [Validators.required]),
    });
    this.feeTypes = await this._businessFeeService.getFeeTypes();
    this.aircraftTypes = this.sortAircraftType(await this._businessFeeService.getAircraftTypes());
    this.aircraftTypes.splice(this.aircraftTypes.length - 1 , 1);
    if (this.isNew) {
      const feeControl = this.formGroup.get('fee');
      feeControl.disable();
      const feeBasisControl = this.formGroup.get('feeBasis');
      feeBasisControl.disable();
    }
  }

  feeChargedClick() {
    const feeControl = this.formGroup.get('fee');
    if (feeControl.value <= 1) {
      this.formGroup.patchValue({fee: 1});
    }
    const feeBasisControl = this.formGroup.get('feeBasis');
    if (feeBasisControl.value === 'No Fee' || feeBasisControl.value === 'Fee Not Disclosed') {
      this.formGroup.patchValue({feeBasis: null});
    }
    feeControl.enable();
    feeBasisControl.enable();
  }

  feeNotChargedClick() {
    this.formGroup.patchValue({fee: null});
    this.formGroup.patchValue({feeBasis: 'No Fee'});
    const feeControl = this.formGroup.get('fee');
    feeControl.disable();
    const feeBasisControl = this.formGroup.get('feeBasis');
    feeBasisControl.disable();
  }

  feeNotDisclosedClick() {
    this.formGroup.patchValue({fee: null});
    this.formGroup.patchValue({feeBasis: 'Fee Not Disclosed'});
    const feeControl = this.formGroup.get('fee');
    feeControl.disable();
    const feeBasisControl = this.formGroup.get('feeBasis');
    feeBasisControl.disable();
  }

  feeBasisChanged() {
    if (this.selectedFeeBasis === 'No Fee') {
      this.formGroup.patchValue({feeRadio: 'FeeNotCharged'});
      this.feeNotChargedClick();
    }
    if (this.selectedFeeBasis === 'Fee Not Disclosed') {
      this.formGroup.patchValue({feeRadio: 'FeeNotDisclosed'});
      this.feeNotDisclosedClick();
    }
  }

  sortAircraftType(aircraftTypes: string[]): string[] {
    return aircraftTypes.sort((a, b) => +(b === 'All Aircraft') - +(a === 'All Aircraft')
      || +(b === 'Single-Engine Piston') - +(a === 'Single-Engine Piston')
      || +(b === 'Multi-Engine Piston') - +(a === 'Multi-Engine Piston')
      || +(b === 'Single-Engine Turboprop') - +(a === 'Single-Engine Turboprop')
      || +(b === 'Multi-Engine Turboprop') - +(a === 'Multi-Engine Turboprop')
      || +(b === 'Small Jet (up to 16K lbs.)') - +(a === 'Small Jet (up to 16K lbs.)')
      || +(b === 'Medium Jet (16K – 40K lbs.)') - +(a === 'Medium Jet (16K – 40K lbs.)')
      || +(b === 'Large Jet (40K lbs. and up)') - +(a === 'Large Jet (40K lbs. and up)')
      || +(a > b) || -(a < b));
  }

  /**
   * triggered by save button click event
   * @return {Promise<void>}
   */
  async save() {
    try {
      let res: any;
      this.fee.description = this.formGroup.get('description').value;
      this.fee.fee = this.formGroup.get('fee').value;
      this.fee.feeBasis = BusinessService.parseFeeBasis(this.formGroup.get('feeBasis').value);
      this.fee.aircraftType = this.formGroup.get('aircraftType').value;
      this.fee.serviceNote = this.formGroup.get('note').value;
      this.fee.waiver = this.formGroup.get('waiver').value;
      this.fee.waiverFuelQty = this.formGroup.get('waiverFuelQty').value;
      if (this.isNew) {
        res = await this._businessFeeService.createBusinessService(this.busID, this.fee);
        this.fee = BusinessService.parse(res);
      } else {
        res = await this._businessFeeService.updateBusinessService(this.busID, this.fee.serviceId, this.fee);
        Object.assign(this.fee, BusinessService.parse(res));
        this.fee = BusinessService.parse(res);
      }
      this.close({cancelBtnClicked: false, success: true, fee: this.fee});
    } catch (e) {
      this.close({cancelBtnClicked: false, success: false});
    }
  }

  /**
   * triggered by cancel button click event
   */
  cancel () {
    this.close({cancelBtnClicked: true});
  }

  /**
   * closes modal
   * @param data
   */
  close(data: any) {
    if (data && data.cancelBtnClicked) {
      Object.assign(this.fee, this.originalFee);
    }
    data['isNew'] = this.isNew;
    this._matDialogRef.close(data);
  }
}
